/**
 * JINS x Toy Story Landing Page
 * JavaScript for Swiper carousels, IntersectionObserver animations, and smooth scroll
 */

// ============================================
// UTILITY FUNCTIONS
// ============================================
const escapeHtml = (str) => {
  if (!str) return '';
  return str.replace(/[&<>"']/g, (m) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[m]));
};

const deepMerge = (target, source) => {
  const output = { ...target };
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      output[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      output[key] = source[key];
    }
  }
  return output;
};

// ============================================
// ASSET PATH CONFIGURATION
// ============================================
// Shopify CDN path for Content > Files
const ASSET_BASE = 'https://cdn.shopify.com/s/files/1/0562/8903/4283/files/';

// ============================================
// SWIPER CONFIGURATION
// ============================================
const SLIDER_DEFAULTS = {
  loop: true,
  speed: 500,
  spaceBetween: 12,
  centeredSlides: true,
  slidesPerView: 1,
  pagination: {},
  navigation: {},
};

// ============================================
// SLIDER DATA - Product detail images and captions
// ============================================
const SLIDER_DATA = {
  // ==================== WOODY ====================
  "slider_01_01": {
    slides: [
      {
        src: ASSET_BASE + "ts-UMF-25A-233_284_01.webp",
        alt: "Woody Brown Orange - Front view",
        caption: "Wellington-style frame inspired by Woody"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-233_284_02.webp",
        alt: "Woody Brown Orange - Button detail",
        caption: "Button-style metal accents"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-233_284_03.webp",
        alt: "Woody Brown Orange - Temple detail",
        caption: "Temples inspired by Woody's outfit"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-233_284_04.webp",
        alt: "Woody Brown Orange - Temple end detail",
        caption: "Finished with a sheriff's badge and Andy's signature at the temple tips, plus cow-print metal details like the ones on Woody's vest."
      }
    ],
    options: {}
  },
  "slider_01_02": {
    slides: [
      {
        src: ASSET_BASE + "ts-UMF-25A-233_158_01.webp",
        alt: "Woody Navy - Front view",
        caption: "Wellington-style frame inspired by Woody"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-233_158_02.webp",
        alt: "Woody Navy - Button detail",
        caption: "Button-style metal accents"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-233_158_03.webp",
        alt: "Woody Navy - Temple detail",
        caption: "Temples inspired by Woody's outfit"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-233_158_04.webp",
        alt: "Woody Navy - Temple end detail",
        caption: "Finished with a sheriff's badge and Andy's signature at the temple tips, plus cow-print metal details like the ones on Woody's vest."
      }
    ],
    options: {}
  },

  // ==================== BO PEEP ====================
  "slider_02_01": {
    slides: [
      {
        src: ASSET_BASE + "ts-UMF-25A-234_203_01.webp",
        alt: "Bo Peep Pink - Front view",
        caption: "A glamorous Boston-style frame inspired by Bo Peep"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-234_203_02.webp",
        alt: "Bo Peep Pink - Bridge detail",
        caption: "The bridge features engraved detailing inspired by the lace of her skirt, with sheep-pattern engravings on the hinges"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-234_203_03.webp",
        alt: "Bo Peep Pink - Temple detail",
        caption: "Temples are modeled after Bo Peep’s outfit, with a smooth finish that evokes the glossy sheen of porcelain"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-234_203_04.webp",
        alt: "Bo Peep Pink - Temple end detail",
        caption: "Temple tips feature designs inspired by Bo Peep’s sheep"
      }
    ],
    options: {}
  },
  "slider_02_02": {
    slides: [
      {
        src: ASSET_BASE + "ts-UMF-25A-234_151_01.webp",
        alt: "Bo Peep Sky Blue - Front view",
        caption: "A glamorous Boston-style frame inspired by Bo Peep"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-234_151_02.webp",
        alt: "Bo Peep Sky Blue - Bridge detail",
        caption: "The bridge features engraved detailing inspired by the lace of her skirt, with sheep-pattern engravings on the hinges"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-234_151_03.webp",
        alt: "Bo Peep Sky Blue - Temple detail",
        caption: "Temples are modeled after Bo Peep’s outfit, with a smooth finish that evokes the glossy sheen of porcelain"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-234_151_04.webp",
        alt: "Bo Peep Sky Blue - Temple end detail",
        caption: "Temple tips feature designs inspired by Bo Peep’s sheep"
      }
    ],
    options: {}
  },

  // ==================== BUZZ LIGHTYEAR ====================
  "slider_03_01": {
    slides: [
      {
        src: ASSET_BASE + "ts-URF-25A-235_200_01.webp",
        alt: "Buzz Clear White - Front view",
        caption: "Wellington-style frames inspired by Buzz Lightyear"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-235_200_02.webp",
        alt: "Buzz Clear White - Button detail",
        caption: "Both sides feature a design based on the buttons on Buzz Lightyear’s chest"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-235_200_03.webp",
        alt: "Buzz Clear White - Temple detail",
        caption: "The temples display the Space Ranger emblem, with Buzz Lightyear’s wing colors printed on the outside"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-235_200_04.webp",
        alt: "Buzz Clear White - Temple end detail",
        caption: "Temple tips are shaped to resemble Buzz Lightyear’s wings, and feature Andy’s signature, showing how he's grown since his days with Woody."
      }
    ],
    options: {}
  },
  "slider_03_02": {
    slides: [
      {
        src: ASSET_BASE + "ts-URF-25A-235_192_01.webp",
        alt: "Buzz Clear Grey - Front view",
        caption: "Wellington-style frames inspired by Buzz Lightyear"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-235_192_02.webp",
        alt: "Buzz Clear Grey - Button detail",
        caption: "Both sides feature a design based on the buttons on Buzz Lightyear’s chest"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-235_192_03.webp",
        alt: "Buzz Clear Grey - Temple detail",
        caption: "The temples display the Space Ranger emblem, with Buzz Lightyear’s wing colors printed on the outside"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-235_192_04.webp",
        alt: "Buzz Clear Grey - Temple end detail",
        caption: "Temple tips are shaped to resemble Buzz Lightyear’s wings, and feature Andy’s signature, showing how he's grown since his days with Woody."
      }
    ],
    options: {}
  },

  // ==================== ALIENS ====================
  "slider_04_01": {
    slides: [
      {
        src: ASSET_BASE + "ts-URF-25A-236_256_01.webp",
        alt: "Aliens Blue - Front view",
        caption: "Boston-style frames inspired by the Aliens"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_256_02.webp",
        alt: "Aliens Blue - Eye detail",
        caption: "Engraved detailing inspired by the Aliens’ eyes"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_256_03.webp",
        alt: "Aliens Blue - Temple detail",
        caption: "The outer side features engravings of the Pizza Planet rocket, where the Aliens were first introduced."
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_256_04.webp",
        alt: "Aliens Blue - Temple end detail",
        caption: "Temple tips showcase a fun, character-inspired face design"
      }
    ],
    options: {}
  },
  "slider_04_02": {
    slides: [
      {
        src: ASSET_BASE + "ts-URF-25A-236_128_01.webp",
        alt: "Aliens Green - Front view",
        caption: "Boston-style frames inspired by the Aliens"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_128_02.webp",
        alt: "Aliens Green - Eye detail",
        caption: "Engraved detailing inspired by the Aliens’ eyes"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_128_03.webp",
        alt: "Aliens Green - Temple detail",
        caption: "The outer side features engravings of the Pizza Planet rocket, where the Aliens were first introduced."
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_128_04.webp",
        alt: "Aliens Green - Temple end detail",
        caption: "Temple tips showcase a fun, character-inspired face design"
      }
    ],
    options: {}
  },

  // ==================== HAMM ====================
  "slider_05_01": {
    slides: [
      {
        src: ASSET_BASE + "ts-URF-25A-236_202_01.webp",
        alt: "Hamm Matte Pink - Front view",
        caption: "Boston-style frames inspired by Hamm"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_202_02.webp",
        alt: "Hamm Matte Pink - Shape detail",
        caption: "Soft, rounded lines throughout capture Hamm’s plump, coin-bank shape, with ear-shaped metal accents at the hinges"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_202_03.webp",
        alt: "Hamm Matte Pink - Temple detail",
        caption: "Temples feature illustrations of Hamm"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_202_04.webp",
        alt: "Hamm Matte Pink - Temple end detail",
        caption: "Temple tips are inspired by Hamm’s arms and legs, with metal detailing resembling him holding his coin"
      }
    ],
    options: {}
  },
  "slider_05_02": {
    slides: [
      {
        src: ASSET_BASE + "ts-URF-25A-236_176_01.webp",
        alt: "Hamm Clear Pink - Front view",
        caption: "Boston-style frames inspired by Hamm"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_176_02.webp",
        alt: "Hamm Clear Pink - Shape detail",
        caption: "Soft, rounded lines throughout capture Hamm’s plump, coin-bank shape, with ear-shaped metal accents at the hinges"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_176_03.webp",
        alt: "Hamm Clear Pink - Temple detail",
        caption: "Temples feature illustrations of Hamm"
      },
      {
        src: ASSET_BASE + "ts-URF-25A-236_176_04.webp",
        alt: "Hamm Clear Pink - Temple end detail",
        caption: "Temple tips are inspired by Hamm’s arms and legs, with metal detailing resembling him holding his coin"
      }
    ],
    options: {}
  },

  // ==================== REX ====================
  "slider_06_01": {
    slides: [
      {
        src: ASSET_BASE + "ts-UMF-25A-238_239_01.webp",
        alt: "Rex Clear Green - Front view",
        caption: "Boston-style frames inspired by Rex"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-238_239_02.webp",
        alt: "Rex Clear Green - Scale detail",
        caption: "Hinges inspired by Rex’s hands, with engraved teeth details on the bridge"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-238_239_03.webp",
        alt: "Rex Clear Green - Temple detail",
        caption: "A three-dimensional design that captures Rex’s distinctive texture"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-238_239_04.webp",
        alt: "Rex Clear Green - Temple end detail",
        caption: "Temple tips inspired by Rex’s feet"
      }
    ],
    options: {}
  },
  "slider_06_02": {
    slides: [
      {
        src: ASSET_BASE + "ts-UMF-25A-238_196_01.webp",
        alt: "Rex Black - Front view",
        caption: "Boston-style frames inspired by Rex"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-238_196_02.webp",
        alt: "Rex Black - Scale detail",
        caption: "Hinges inspired by Rex’s hands, with engraved teeth details on the bridge"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-238_196_03.webp",
        alt: "Rex Black - Temple detail",
        caption: "A three-dimensional design that captures Rex’s distinctive texture"
      },
      {
        src: ASSET_BASE + "ts-UMF-25A-238_196_04.webp",
        alt: "Rex Black - Temple end detail",
        caption: "Temple tips inspired by Rex’s feet"
      }
    ],
    options: {}
  }
};

// ============================================
// SLIDER RENDERER
// ============================================
const renderSlider = (container, data) => {
  container.innerHTML = "";

  // Create wrapper
  const wrapper = document.createElement("div");
  wrapper.className = "p-slide__track swiper-wrapper";

  // Create slides
  (data.slides || []).forEach(item => {
    const slide = document.createElement("div");
    slide.className = "p-slide__item swiper-slide";
    slide.innerHTML = `
      <img loading="lazy"
           src="${item.src}"
           alt="${escapeHtml(item.alt || "")}"
           class="p-slide__image">
      <p class="p-slide__text">${item.caption || ""}</p>
    `;
    wrapper.appendChild(slide);
  });

  // Create navigation
  const navWrap = document.createElement("div");
  navWrap.className = "swiper-nav-wrap";

  const prev = document.createElement("div");
  prev.className = "swiper-button-prev";
  prev.setAttribute("aria-label", "Previous slide");

  const pag = document.createElement("div");
  pag.className = "swiper-pagination";
  pag.setAttribute("aria-label", "Slide pagination");

  const next = document.createElement("div");
  next.className = "swiper-button-next";
  next.setAttribute("aria-label", "Next slide");

  navWrap.append(prev, pag, next);
  container.append(wrapper, navWrap);

  // Merge options and initialize Swiper
  const opts = deepMerge(SLIDER_DEFAULTS, data.options || {});
  opts.centeredSlides = true;
  opts.pagination = { el: pag, clickable: true };
  opts.navigation = { nextEl: next, prevEl: prev };

  return new Swiper(container, opts);
};

// ============================================
// INTERSECTION OBSERVER FOR ANIMATIONS
// ============================================
(() => {
  const observerPool = new Map();

  const parseThreshold = (val) => {
    if (!val) return 0.2;
    if (val.includes(',')) return val.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
    const n = Number(val);
    return isNaN(n) ? 0.2 : n;
  };

  const getObserverKey = (rootMargin, threshold) =>
    `${rootMargin}|${Array.isArray(threshold) ? threshold.join('/') : threshold}`;

  const getObserver = (rootMargin = '0px 0px 0px 0px', threshold = 0.2) => {
    const key = getObserverKey(rootMargin, threshold);
    if (observerPool.has(key)) return observerPool.get(key);

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const el = entry.target;
        const cls = el.dataset.inviewClass || 'is-inview';
        const once = el.dataset.inviewOnce !== 'false';
        const out = el.dataset.inviewOut === 'true';

        if (entry.isIntersecting) {
          el.classList.add(cls);
          if (once && !out) obs.unobserve(el);
        } else if (out) {
          el.classList.remove(cls);
        }
      });
    }, { root: null, rootMargin, threshold });

    observerPool.set(key, obs);
    return obs;
  };

  const observeElement = (el) => {
    if (!el || el.dataset.inviewBound === 'true') return;

    const rootMargin = el.dataset.inviewOffset || '0px 0px 0px 0px';
    const threshold = parseThreshold(el.dataset.inviewThreshold);

    const obs = getObserver(rootMargin, threshold);
    obs.observe(el);
    el.dataset.inviewBound = 'true';
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-inview]').forEach(observeElement);
  };

  window.InView = { init, observe: observeElement };

  document.addEventListener('DOMContentLoaded', () => init());
})();

// ============================================
// SMOOTH SCROLL
// ============================================
(() => {
  const DEFAULT_OFFSET = 0;

  const getOffset = (el) => {
    const v = el?.dataset?.scrollOffset
      ?? document.documentElement.dataset.scrollOffset
      ?? DEFAULT_OFFSET;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };

  const scrollToHash = (hash, offset = 0, { replace = false } = {}) => {
    if (!hash || hash === '#') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const url = location.pathname + location.search + '#';
      (replace ? history.replaceState : history.pushState).call(history, null, '', url);
      return;
    }

    const raw = hash.slice(1);
    const id = decodeURIComponent(raw);
    const el = document.getElementById(id);
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.pageYOffset - offset;

    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });

    window.scrollTo({ top: y, behavior: 'smooth' });
    const url = location.pathname + location.search + '#' + encodeURIComponent(id);
    (replace ? history.replaceState : history.pushState).call(history, null, '', url);
  };

  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    if (a.dataset.noSmooth === 'true') return;

    e.preventDefault();
    const href = a.getAttribute('href');
    const offset = getOffset(a);
    scrollToHash(href, offset, { replace: false });
  });

  window.addEventListener('load', () => {
    if (location.hash) {
      const offset = getOffset(document.documentElement);
      scrollToHash(location.hash, offset, { replace: true });
    }
  });
})();

// ============================================
// STICKER SPRITE ANIMATIONS
// ============================================
(() => {
  const initStickerAnimation = () => {
    const stickerObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sticker = entry.target;
          sticker.classList.add('is-animating');
          stickerObserver.unobserve(sticker);
        }
      });
    }, {
      rootMargin: '0px 0px 0px 0px',
      threshold: 0.1
    });

    const stickers = document.querySelectorAll('.p-character__board__sticker');
    stickers.forEach(sticker => {
      stickerObserver.observe(sticker);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickerAnimation);
  } else {
    initStickerAnimation();
  }
})();

// ============================================
// INITIALIZE SLIDERS ON DOM READY
// ============================================
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll('.p-slide.swiper[data-slide]').forEach(container => {
    const id = container.dataset.slide;
    const conf = SLIDER_DATA[id];
    if (!conf) {
      console.warn(`[Toy Story] Slider data not found for "${id}"`, container);
      return;
    }
    renderSlider(container, conf);
  });
});
